import type { Job } from "@facade/contracts";
import { useTranslation } from "react-i18next";

type JobProgressDetailsProps = {
  job: Job;
};

export function JobProgressDetails({ job }: JobProgressDetailsProps) {
  const { t } = useTranslation();
  const progress = job.progress;

  if (!progress) return null;

  const percentage = Math.min(
    100,
    Math.max(0, Math.round((progress.current / progress.total) * 100)),
  );
  const remainingSeconds = estimateRemainingSeconds(job);
  const isUploading = progress.phase === "uploading";
  const isThumbnail = progress.phase === "IFC_THUMBNAIL";

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      <div
        role="progressbar"
        aria-label={t("jobs.progress.ariaLabel")}
        aria-valuemin={0}
        aria-valuemax={progress.total}
        aria-valuenow={progress.current}
        className="h-1.5 overflow-hidden rounded-full bg-muted"
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div className="flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
        <span>
          {isThumbnail
            ? t("jobs.progress.thumbnail")
            : isUploading
              ? t("jobs.progress.finalizing")
              : t("jobs.progress.count", {
                  current: progress.current,
                  total: progress.total,
                })}
        </span>
        <span className="shrink-0">
          {remainingSeconds === null
            ? `${percentage}%`
            : formatRemainingTime(remainingSeconds, t)}
        </span>
      </div>
    </div>
  );
}

function estimateRemainingSeconds(job: Job): number | null {
  const progress = job.progress;
  if (
    !progress ||
    progress.phase !== "processing" ||
    !job.startedAt ||
    progress.current < 5 ||
    progress.current >= progress.total
  ) {
    return null;
  }

  const elapsedSeconds =
    (new Date(progress.updatedAt).getTime() -
      new Date(job.startedAt).getTime()) /
    1000;
  if (!Number.isFinite(elapsedSeconds) || elapsedSeconds < 5) return null;

  const entitiesPerSecond = progress.current / elapsedSeconds;
  if (entitiesPerSecond <= 0) return null;

  const estimate = (progress.total - progress.current) / entitiesPerSecond;
  return Number.isFinite(estimate) && estimate > 0
    ? Math.min(estimate, 7 * 24 * 60 * 60)
    : null;
}

function formatRemainingTime(
  remainingSeconds: number,
  t: ReturnType<typeof useTranslation>["t"],
) {
  if (remainingSeconds < 60) return t("jobs.progress.lessThanMinute");

  const minutes = Math.ceil(remainingSeconds / 60);
  if (minutes < 60)
    return t("jobs.progress.minutesRemaining", { count: minutes });

  const hours = Math.floor(minutes / 60);
  const remainderMinutes = minutes % 60;
  return t("jobs.progress.hoursRemaining", {
    hours,
    minutes: remainderMinutes,
  });
}
